import { Headphones, Phone, PhoneOutgoing, Undo2, UserPlus } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAddOutsideParticipant, useEndOutsideParticipant, useRoomCall, useSpeedDials } from "@/lib/queries";
import {
  directRoomCallId, listedRoomCallId, participantEndedText, participantState,
  type ParticipantState,
} from "@/lib/rooms";
import type { ConsultOutcome, SpeedDial } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";
import { BriefingEndings } from "@/softphone/acd-call";
import { useSoftphone } from "@/softphone/softphone-context";

/**
 * An outside number brought into a call (ADR-065): the Add participant dialog, and the bar that
 * follows the newcomer from the first ring to the end of the briefing.
 *
 * The flow is the warm transfer's (Collins: "follow same warm transfer"): their phone rings from
 * the call, the caller goes on hold the moment they answer, the reps talk to them alone, and the
 * same endings follow — Merge all, Take the caller back, and Connect them & leave on a call on
 * the rep's own number. On a campaign call the last rep leaving ends the call for the caller, so
 * leaving is not offered there.
 *
 * Nothing here is gated on which service rings the number. The gate is the room the phone's leg
 * is in (`listedRoomCallId`): a campaign call's or a direct call's, both of which the server's
 * picture of the room (`GET /phone/room/calls/{id}`) describes the same way.
 */

/**
 * The newcomer the rep just asked for, until the server's picture of the room has them.
 *
 * `POST /phone/room/outside` names the newcomer's leg before it exists; the event-tracker files
 * it as an invite a moment later, when the switch reports the leg. That moment is a blank on the
 * bar without this, and a request the switch never acted on would be silence forever, so the
 * bar shows "Ringing …" from the answer and gives up after `ASKED_FOR_MS` with nothing to show.
 */
interface Asked {
  callId: string;
  channelId: string;
  name: string;
  number: string;
  at: number;
}

const ASKED_FOR_MS = 12_000;
let asked: Asked | null = null;
const listeners = new Set<() => void>();

function rememberAsked(next: Asked | null) {
  asked = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** For tests and the gallery: nothing asked for. */
export function forgetParticipant() {
  rememberAsked(null);
}

export interface Participant {
  state: ParticipantState | null;
  /** The call lives in a direct room: leaving is an ending there. */
  isDirect: boolean;
  /** Something is under way — ringing or talking — so the call's other controls wait. */
  pending: boolean;
}

/**
 * The outside participant on this call, from the server's picture of the room, with the one the
 * rep just asked for standing in until the room has them.
 */
export function useParticipant(callId: string | null): Participant {
  const phone = useSoftphone();
  const listed = callId !== null && listedRoomCallId(phone.conference) === callId;
  const isDirect = callId !== null && directRoomCallId(phone.conference) === callId;
  const { data: room } = useRoomCall(callId, { enabled: listed });
  const local = React.useSyncExternalStore(subscribe, () => asked, () => asked);
  // The stand-in expires on its own: a tick so the bar redraws when it does.
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!local) return;
    const left = local.at + ASKED_FOR_MS - Date.now();
    const timer = window.setTimeout(() => {
      if (asked === local) rememberAsked(null);
      tick();
    }, Math.max(0, left));
    return () => window.clearTimeout(timer);
  }, [local]);

  if (!listed) return { state: null, isDirect, pending: false };
  let state = participantState(room);
  const known = room?.invites.some((i) => i.channel_id === local?.channelId) ||
    room?.members.some((m) => m.channel_id === local?.channelId);
  if (local && local.callId === callId && known) {
    // The room has them now: the stand-in has done its job.
    rememberAsked(null);
  } else if (
    local && local.callId === callId && state === null && Date.now() - local.at < ASKED_FOR_MS
  ) {
    state = { phase: "ringing", channelId: local.channelId, name: local.name, number: local.number };
  }
  return { state, isDirect, pending: state !== null && state.phase !== "ended" };
}

/**
 * What an outside participant takes out of the rep's hands on the call, with the reason for
 * each, or null when nothing is under way. Hold: the caller is on hold for the briefing and the
 * server refuses a hold or unhold meanwhile (the ACD's `call_hold`, as for a closer's briefing).
 * Transfer: a handoff started now would take the caller off hold under the newcomer's feet.
 */
export function participantLocks(
  participant: Participant,
): { hold: string; transfer: string; add: string } | null {
  const { state } = participant;
  if (!state || state.phase === "ended") return null;
  return state.phase === "ringing"
    ? {
        hold: `${state.name}'s phone is ringing`,
        transfer: `Finish with ${state.name} first`,
        add: `${state.name}'s phone is ringing`,
      }
    : {
        hold: `The caller is on hold while you talk to ${state.name}`,
        transfer: `Finish with ${state.name} first`,
        add: `Finish with ${state.name} first`,
      };
}

/**
 * The newcomer's progress on the call screen: ringing (and a way to stop), the briefing and its
 * endings, or for a short while why a ring came to nothing.
 */
export function ParticipantBar({ callId }: { callId: string }) {
  const toast = useToast();
  const { state, isDirect } = useParticipant(callId);
  const finish = useEndOutsideParticipant();
  const [error, setError] = React.useState<string | null>(null);
  const [dismissed, setDismissed] = React.useState<string | null>(null);

  if (state === null) return null;
  if (state.phase === "ended") {
    if (dismissed === state.channelId) return null;
    const words = participantEndedText(state.cause, state.name);
    return (
      <div className="w-full rounded-lg border border-border bg-surface-sunken px-3 py-2 text-left text-xs">
        <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
          <PhoneOutgoing className="size-3.5 shrink-0 text-text-muted" aria-hidden />
          <span className="truncate">{words.title}</span>
        </p>
        <p className="mt-0.5 text-text-muted">{words.detail}</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 h-7 px-2 text-xs"
          onClick={() => setDismissed(state.channelId)}
        >
          Dismiss
        </Button>
      </div>
    );
  }

  const act = (outcome: ConsultOutcome) => {
    setError(null);
    finish.mutate(
      { callId, channelId: state.channelId, outcome },
      {
        onSuccess: () => {
          if (outcome === "complete") toast.success("Connected", `${state.name} has the caller.`);
          else if (outcome === "join") {
            toast.success("Merged", `You, ${state.name} and the caller are all on the call.`);
          } else if (state.phase === "ringing") {
            toast.info("Stopped ringing", `${state.name} was not added.`);
            rememberAsked(null);
          } else toast.success("Taken back", "The caller is with you again.");
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "Could not do that just now."),
      },
    );
  };
  const busy = (outcome: ConsultOutcome) =>
    finish.isPending && finish.variables?.outcome === outcome;

  return (
    <div className="w-full rounded-lg border border-accent-8 bg-surface-sunken px-3 py-2 text-left text-xs">
      {state.phase === "ringing" ? (
        <>
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
            <Spinner className="size-3.5 shrink-0" />
            <span className="truncate">Ringing {state.name}…</span>
          </p>
          <p className="mt-0.5 text-text-muted">
            Your caller notices nothing until they answer; then they go on hold while you talk.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-2 h-9 w-full"
            loading={busy("cancel")}
            onClick={() => act("cancel")}
          >
            <Undo2 />
            Stop ringing them
          </Button>
        </>
      ) : (
        <>
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
            <Headphones className="size-3.5 shrink-0 text-text-muted" aria-hidden />
            <span className="truncate">You are talking to {state.name}</span>
          </p>
          <p className="mt-0.5 text-text-muted">Your caller is on hold and cannot hear you.</p>
          <BriefingEndings busy={busy} onEnd={act} leave={isDirect} />
        </>
      )}
      {error && (
        <Callout tone="danger" className="mt-2">
          {error}
        </Callout>
      )}
    </div>
  );
}

/**
 * Add participant: a speed dial, or a number typed. Centred over the console like Transfer, with
 * the call still on screen behind it. Closed the moment the request is accepted; the bar on the
 * call screen carries it from there.
 */
export function AddParticipantDialog({
  open, callId, onClose,
}: {
  open: boolean;
  callId: string | null;
  onClose: () => void;
}) {
  const title = React.useRef<HTMLHeadingElement>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          title.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle ref={title} tabIndex={-1} className="outline-none">
            Add participant
          </DialogTitle>
          <DialogDescription>
            They are rung from this call. When they answer, your caller goes on hold while you
            talk to them; then merge everyone, or take the caller back.
          </DialogDescription>
        </DialogHeader>
        {/* Keyed on open so every add starts on a clean form. */}
        {open && callId !== null && <AddParticipantForm callId={callId} onDone={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function AddParticipantForm({ callId, onDone }: { callId: string; onDone: () => void }) {
  const toast = useToast();
  const add = useAddOutsideParticipant();
  const speedDials = useSpeedDials();
  const [number, setNumber] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const id = React.useId();

  const ring = (input: { speedDialId?: number; number?: string }, name: string | null) => {
    setError(null);
    add.mutate(
      { callId, ...input },
      {
        onSuccess: (result) => {
          const who = result.name ?? formatPhoneNumber(result.number);
          rememberAsked({
            callId, channelId: result.channel_id, name: who, number: result.number, at: Date.now(),
          });
          toast.success(`Ringing ${who}`, "When they answer, your caller goes on hold while you talk.");
          onDone();
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : `Could not ring ${name ?? "them"}.`),
      },
    );
  };
  const dialing = (dial: SpeedDial) =>
    add.isPending && add.variables?.speedDialId === dial.id;
  const typed = add.isPending && add.variables?.speedDialId == null;

  return (
    <DialogBody className="flex flex-col gap-3">
      {error && <Callout tone="danger">{error}</Callout>}
      <div role="group" aria-label="Speed dials" className="flex flex-col gap-1.5">
        <p className="text-xs font-medium text-text-muted" aria-hidden>
          Speed dials
        </p>
        {speedDials.isPending ? (
          <Spinner className="size-4" />
        ) : (speedDials.data ?? []).length === 0 ? (
          <p className="text-xs text-text-muted">
            No speed dials yet. An admin adds them under Contact Center.
          </p>
        ) : (
          (speedDials.data ?? []).map((dial) => (
            <button
              key={dial.id}
              type="button"
              onClick={() => ring({ speedDialId: dial.id }, dial.label)}
              disabled={add.isPending}
              aria-busy={dialing(dial) || undefined}
              aria-label={`Dial ${dial.label}, ${formatPhoneNumber(dial.number)}`}
              className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-left transition-colors hover:bg-surface-hover disabled:cursor-wait disabled:opacity-60"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-3 text-accent-11">
                {dialing(dial) ? <Spinner /> : <Phone className="size-4" aria-hidden />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium leading-snug text-text">
                  Dial {dial.label}
                </span>
                <span className="block text-xs text-text-muted">
                  {formatPhoneNumber(dial.number)}
                </span>
              </span>
            </button>
          ))
        )}
      </div>
      <form
        role="group"
        aria-label="A number"
        className="flex flex-col gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (number.trim()) ring({ number: number.trim() }, null);
        }}
      >
        <label htmlFor={id} className="text-xs font-medium text-text-muted">
          A number
        </label>
        <div className="flex gap-1.5">
          <Input
            id={id}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="+1 952 994 1286"
            inputMode="tel"
            autoComplete="off"
            className="h-9 text-sm"
          />
          <Button type="submit" className="h-9 shrink-0" disabled={!number.trim()} loading={typed}>
            <UserPlus />
            Add
          </Button>
        </div>
        <p className="text-xs text-text-muted">US and Canadian numbers.</p>
      </form>
    </DialogBody>
  );
}
